<template>
	<div class="draw-history-section">
		<div class="history-header">
			<div class="header-left">
				<i class="el-icon-time"></i>
				<span class="header-title">历史画板</span>
				<span class="header-count" v-if="records.length > 0">{{ records.length }} 张</span>
			</div>
			<div class="header-right">
				<el-radio-group v-model="viewMode" size="mini">
					<el-radio-button label="grid">
						<i class="el-icon-s-grid"></i>
					</el-radio-button>
					<el-radio-button label="list">
						<i class="el-icon-s-unfold"></i>
					</el-radio-button>
				</el-radio-group>
				<el-button type="text" size="mini" @click="refreshHistory" :loading="loading">
					<i class="el-icon-refresh"></i> 刷新
				</el-button>
			</div>
		</div>

		<!-- 网格视图 -->
		<div v-if="viewMode === 'grid'" class="history-grid" v-infinite-scroll="loadMore" :infinite-scroll-disabled="noMore">
			<div
				v-for="item in records"
				:key="item.guid"
				class="history-card"
				@click="previewImage(item)"
			>
				<div class="card-image-wrap">
					<!-- 成功状态的图片 -->
					<el-image
						v-if="item.workStatus === 'success'"
						:src="item.img"
						fit="cover"
						class="card-image"
					>
						<div slot="placeholder" class="image-placeholder">
							<i class="el-icon-loading"></i>
						</div>
						<div slot="error" class="image-error">
							<i class="el-icon-picture-outline"></i>
						</div>
					</el-image>

					<!-- 生成中状态 -->
					<div v-else-if="item.workStatus === 'doing' || item.workStatus === 'wait'" class="status-overlay doing">
						<div class="status-icon">
							<div class="pulse-ring"></div>
							<i class="el-icon-loading"></i>
						</div>
						<span class="status-text">生成中...</span>
					</div>

					<!-- 失败状态 -->
					<div v-else class="status-overlay fail">
						<i class="el-icon-warning-outline"></i>
						<span class="status-text">生成失败</span>
					</div>

					<!-- 悬停操作层 -->
					<div class="card-actions" v-if="item.workStatus === 'success'">
						<el-tooltip content="查看大图" placement="top">
							<span class="action-btn" @click.stop="previewImage(item)">
								<i class="el-icon-zoom-in"></i>
							</span>
						</el-tooltip>
						<el-tooltip content="下载图片" placement="top">
							<span class="action-btn" @click.stop="downloadImage(item.img)">
								<i :class="downloadingMap[item.img] ? 'el-icon-loading' : 'el-icon-download'"></i>
							</span>
						</el-tooltip>
						<el-tooltip content="删除" placement="top">
							<span class="action-btn delete" @click.stop="deleteRecord(item)">
								<i class="el-icon-delete"></i>
							</span>
						</el-tooltip>
					</div>
				</div>

				<div class="card-info" v-if="item.workStatus === 'success'">
					<p class="info-prompt" :title="item.title">{{ item.title || '无描述' }}</p>
					<p class="info-time" v-if="item.modifyTime">{{ formatTime(item.modifyTime) }}</p>
				</div>
			</div>

			<!-- 加载更多 -->
			<div v-if="loading" class="loading-more">
				<i class="el-icon-loading"></i> 加载中...
			</div>
			<div v-if="noMore && records.length > 0" class="no-more">没有更多了</div>
		</div>

		<!-- 列表视图 -->
		<div v-else class="history-list" v-infinite-scroll="loadMore" :infinite-scroll-disabled="noMore">
			<div
				v-for="item in records"
				:key="item.guid"
				class="list-item"
				@click="previewImage(item)"
			>
				<div class="list-image-wrap">
					<el-image
						v-if="item.workStatus === 'success'"
						:src="item.img"
						fit="cover"
						class="list-image"
					>
						<div slot="error" class="image-error">
							<i class="el-icon-picture-outline"></i>
						</div>
					</el-image>
					<div v-else-if="item.workStatus === 'doing' || item.workStatus === 'wait'" class="list-status doing">
						<i class="el-icon-loading"></i>
					</div>
					<div v-else class="list-status fail">
						<i class="el-icon-warning-outline"></i>
					</div>
				</div>
				<div class="list-content">
					<p class="list-prompt">{{ item.title || '无描述' }}</p>
					<p class="list-meta">
						<span class="meta-status" :class="item.workStatus">{{ statusText(item.workStatus) }}</span>
						<span class="meta-time">{{ formatTime(item.modifyTime) }}</span>
					</p>
				</div>
				<div class="list-actions" v-if="item.workStatus === 'success'">
					<el-tooltip content="查看大图" placement="top">
						<span class="list-action-btn" @click.stop="previewImage(item)">
							<i class="el-icon-zoom-in"></i>
						</span>
					</el-tooltip>
					<el-tooltip content="下载图片" placement="top">
						<span class="list-action-btn" @click.stop="downloadImage(item.img)">
							<i :class="downloadingMap[item.img] ? 'el-icon-loading' : 'el-icon-download'"></i>
						</span>
					</el-tooltip>
					<el-tooltip content="删除" placement="top">
						<span class="list-action-btn delete" @click.stop="deleteRecord(item)">
							<i class="el-icon-delete"></i>
						</span>
					</el-tooltip>
				</div>
			</div>

			<div v-if="loading" class="loading-more">
				<i class="el-icon-loading"></i> 加载中...
			</div>
			<div v-if="noMore && records.length > 0" class="no-more">没有更多了</div>
		</div>

		<!-- 空状态 -->
		<div v-if="!loading && records.length === 0" class="history-empty">
			<div class="empty-icon">
				<i class="el-icon-picture-outline-round"></i>
			</div>
			<p class="empty-title">暂无历史记录</p>
			<p class="empty-hint">开始创作你的第一幅 AI 画作吧</p>
		</div>

		<!-- 图片预览弹窗 -->
		<el-dialog
			:visible.sync="previewVisible"
			width="auto"
			:fullscreen="false"
			custom-class="image-preview-dialog"
			:show-close="true"
			append-to-body
			center
		>
			<div class="preview-content">
				<div class="preview-image-wrap">
					<el-image :src="previewImageUrl" fit="contain" class="preview-image"></el-image>
				</div>
				<div class="preview-info" v-if="previewItem">
					<div class="preview-info__scroll">
						<p class="preview-prompt">{{ previewItem.title }}</p>
						<p class="preview-meta">
							<span v-if="previewItem.model" class="preview-model"><i class="el-icon-cpu"></i> {{ previewItem.model }}</span>
							<span class="preview-time">{{ formatTime(previewItem.modifyTime) }}</span>
						</p>

						<!-- 参考图（图生图记录才有，旧记录可能没有） -->
						<div class="preview-reference" v-if="previewItem.referenceImage">
							<span class="preview-reference__label">
								<i class="el-icon-picture-outline"></i> 参考图
							</span>
							<el-image
								:src="previewItem.referenceImage"
								fit="cover"
								class="preview-reference__img"
								:preview-src-list="[previewItem.referenceImage]"
							></el-image>
						</div>
					</div>

					<div class="preview-actions">
						<span class="preview-btn primary" :class="{ downloading: downloadingMap[previewImageUrl] }" @click="downloadImage(previewImageUrl)">
							<i :class="downloadingMap[previewImageUrl] ? 'el-icon-loading' : 'el-icon-download'"></i>
							{{ downloadingMap[previewImageUrl] ? '下载中...' : '下载原图' }}
						</span>
						<el-button plain size="small" @click="previewVisible = false">关闭</el-button>
					</div>
				</div>
			</div>
		</el-dialog>
	</div>
</template>

<script>
import { getWorkImages, recordDelApi, downloadDrawImageApi } from '@/apis/draw';
import dayjs from 'dayjs';

export default {
	name: 'layout-draw-history',

	data() {
		return {
			records: [],
			currPage: 0,
			loading: false,
			noMore: false,
			viewMode: 'grid', // grid | list
			previewVisible: false,
			previewImageUrl: '',
			previewItem: null,
			downloadingMap: {},
		};
	},

	async mounted() {
		await this.loadHistory();
	},

	methods: {
		async loadHistory() {
			this.loading = true;
			try {
				const res = await getWorkImages(1);
				if (res && Array.isArray(res)) {
					this.records = res;
					this.currPage = 1;
					this.noMore = res.length < 10;
				}
			} catch (error) {
				console.error('加载历史记录失败:', error);
			} finally {
				this.loading = false;
			}
		},

		async loadMore() {
			if (this.loading || this.noMore) return;
			this.loading = true;
			try {
				const nextPage = this.currPage + 1;
				const res = await getWorkImages(nextPage);
				if (res && Array.isArray(res)) {
					if (res.length === 0) {
						this.noMore = true;
					} else {
						this.records.push(...res);
						this.currPage = nextPage;
						this.noMore = res.length < 10;
					}
				}
			} catch (error) {
				console.error('加载更多失败:', error);
			} finally {
				this.loading = false;
			}
		},

		// 供父组件（image_ready 事件）调用的静默刷新，不弹 toast
		async reload() {
			this.currPage = 0;
			this.noMore = false;
			this.records = [];
			await this.loadHistory();
		},

		async refreshHistory() {
			await this.reload();
			this.$message.success('已刷新');
		},

		async deleteRecord(item) {
			try {
				await this.$confirm('确定要删除这张图片吗？', '提示', {
					confirmButtonText: '确定',
					cancelButtonText: '取消',
					type: 'warning',
				});
				await recordDelApi({ recordGuid: item.guid });
				this.records = this.records.filter(r => r.guid !== item.guid);
				this.$message.success('删除成功');
			} catch (error) {
				// 用户取消或删除失败
			}
		},

		previewImage(item) {
			if (item.workStatus !== 'success') return;
			this.previewItem = item;
			this.previewImageUrl = item.img;
			this.previewVisible = true;
		},

		async downloadImage(url) {
			if (this.downloadingMap[url]) return;
			this.$set(this.downloadingMap, url, true);
			try {
				const blob = await downloadDrawImageApi(url);
				if (!blob) return;
				const blobUrl = URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = blobUrl;
				a.download = 'ai-image-' + Date.now() + '.png';
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				URL.revokeObjectURL(blobUrl);
			} catch {
				this.$message({ message: '下载失败，请稍后重试', type: 'error' });
			} finally {
				this.$delete(this.downloadingMap, url);
			}
		},

		formatTime(time) {
			if (!time) return '';
			return dayjs(time).format('MM-DD HH:mm');
		},

		statusText(status) {
			const map = {
				success: '已完成',
				doing: '生成中',
				wait: '等待中',
				fail: '失败',
			};
			return map[status] || status;
		},
	},
};
</script>

<style lang="scss">
.draw-history-section {
	margin-top: 32px;

	.history-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 16px;
		padding: 0 4px;

		.header-left {
			display: flex;
			align-items: center;
			gap: 8px;

			i {
				font-size: 16px;
				color: #6126f5;
			}

			.header-title {
				font-size: 16px;
				font-weight: 600;
				color: #1a1a2e;
			}

			.header-count {
				font-size: 12px;
				color: #999;
				background: #f0f0f0;
				padding: 2px 8px;
				border-radius: 10px;
			}
		}

		.header-right {
			display: flex;
			align-items: center;
			gap: 12px;

			.el-radio-group {
				.el-radio-button__inner {
					padding: 6px 10px;
				}
			}
		}
	}

	// 网格视图
	.history-grid {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 16px;

		@media (max-width: 1200px) {
			grid-template-columns: repeat(3, 1fr);
		}
		@media (max-width: 768px) {
			grid-template-columns: repeat(2, 1fr);
		}

		.history-card {
			background: #fff;
			border-radius: 12px;
			overflow: hidden;
			box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
			cursor: pointer;
			transition: all 0.3s;

			&:hover {
				transform: translateY(-4px);
				box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
			}

			.card-image-wrap {
				position: relative;
				aspect-ratio: 1;
				overflow: hidden;
				background: #f5f5f5;

				.card-image {
					width: 100%;
					height: 100%;

					img {
						width: 100%;
						height: 100%;
						object-fit: cover;
						transition: transform 0.3s;
					}

					&:hover img {
						transform: scale(1.05);
					}
				}

				.image-placeholder,
				.image-error {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 100%;
					height: 100%;
					color: #ccc;
					font-size: 24px;
				}

				.status-overlay {
					position: absolute;
					inset: 0;
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					gap: 8px;
					background: #f5f5f5;

					&.doing {
						.status-icon {
							position: relative;
							width: 40px;
							height: 40px;
							display: flex;
							align-items: center;
							justify-content: center;

							.pulse-ring {
								position: absolute;
								width: 40px;
								height: 40px;
								border-radius: 50%;
								border: 2px solid #6126f5;
								animation: pulse-ring 1.5s ease-out infinite;
							}

							i {
								font-size: 20px;
								color: #6126f5;
							}
						}

						.status-text {
							font-size: 12px;
							color: #6126f5;
						}
					}

					&.fail {
						i {
							font-size: 28px;
							color: #ff6b6b;
							margin-bottom: 4px;
						}

						.status-text {
							font-size: 12px;
							color: #999;
						}
					}
				}

				.card-actions {
					position: absolute;
					bottom: 0;
					left: 0;
					right: 0;
					display: flex;
					justify-content: center;
					gap: 12px;
					padding: 12px;
					background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
					opacity: 1; // 默认就显示三个操作图标，不再依赖 hover
					transition: opacity 0.3s;

					.action-btn {
						width: 32px;
						height: 32px;
						border-radius: 50%;
						background: rgba(255, 255, 255, 0.95);
						display: flex;
						align-items: center;
						justify-content: center;
						color: #333;
						cursor: pointer;
						transition: all 0.2s;
						text-decoration: none;

						&:hover {
							background: #fff;
							transform: scale(1.1);
						}

						i {
							font-size: 14px;
						}

						&.delete:hover {
							background: #ff6b6b;
							color: #fff;
						}
					}
				}
			}

			.card-info {
				padding: 12px;

				.info-prompt {
					font-size: 13px;
					color: #333;
					margin: 0 0 6px;
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
				}

				.info-time {
					font-size: 11px;
					color: #999;
					margin: 0;
				}
			}
		}
	}

	// 列表视图
	.history-list {
		background: #fff;
		border-radius: 12px;
		overflow: hidden;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

		.list-item {
			display: flex;
			align-items: center;
			padding: 12px 16px;
			border-bottom: 1px solid #f0f0f0;
			cursor: pointer;
			transition: background 0.2s;

			&:hover {
				background: #fafafa;
			}

			&:last-child {
				border-bottom: none;
			}

			.list-image-wrap {
				width: 60px;
				height: 60px;
				border-radius: 8px;
				overflow: hidden;
				flex-shrink: 0;
				margin-right: 12px;
				background: #f5f5f5;

				.list-image {
					width: 100%;
					height: 100%;
				}

				.list-status {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 100%;
					height: 100%;

					&.doing {
						i {
							font-size: 20px;
							color: #6126f5;
							animation: spin 1s linear infinite;
						}
					}

					&.fail {
						i {
							font-size: 20px;
							color: #ff6b6b;
						}
					}
				}
			}

			.list-content {
				flex: 1;
				min-width: 0;

				.list-prompt {
					font-size: 14px;
					color: #333;
					margin: 0 0 6px;
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
				}

				.list-meta {
					display: flex;
					align-items: center;
					gap: 12px;
					margin: 0;

					.meta-status {
						font-size: 11px;
						padding: 2px 8px;
						border-radius: 10px;

						&.success {
							background: #e8f5e9;
							color: #4caf50;
						}
						&.doing,
						&.wait {
							background: #e3f2fd;
							color: #2196f3;
						}
						&.fail {
							background: #ffebee;
							color: #f44336;
						}
					}

					.meta-time {
						font-size: 12px;
						color: #999;
					}
				}
			}

			.list-actions {
				flex-shrink: 0;
				display: flex;
				align-items: center;
				gap: 8px;

				.list-action-btn {
					width: 30px;
					height: 30px;
					border-radius: 8px;
					background: #f5f5f7;
					display: flex;
					align-items: center;
					justify-content: center;
					color: #555;
					cursor: pointer;
					transition: all 0.2s;
					user-select: none;

					i {
						font-size: 14px;
					}

					&:hover {
						background: #6126f5;
						color: #fff;
					}

					&.delete:hover {
						background: #ff6b6b;
					}
				}
			}
		}
	}

	.loading-more,
	.no-more {
		text-align: center;
		padding: 20px;
		color: #999;
		font-size: 13px;
		grid-column: 1 / -1;
	}

	.history-empty {
		text-align: center;
		padding: 60px 20px;
		background: #fff;
		border-radius: 12px;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

		.empty-icon {
			width: 80px;
			height: 80px;
			border-radius: 50%;
			background: linear-gradient(135deg, #f0ebff, #e8e0ff);
			display: flex;
			align-items: center;
			justify-content: center;
			margin: 0 auto 16px;

			i {
				font-size: 36px;
				color: #9b6ff5;
			}
		}

		.empty-title {
			font-size: 16px;
			font-weight: 600;
			color: #333;
			margin: 0 0 8px;
		}

		.empty-hint {
			font-size: 13px;
			color: #999;
			margin: 0;
		}
	}
}

// 图片预览弹窗
.image-preview-dialog {
	width: min(880px, 80vw) !important; // 直接约束 el-dialog 宽度，避免白底两侧溢出
	margin: 8vh auto !important; // 横向 auto 居中（Element 默认行为）；纵向略上移避免大屏空隙
	background: transparent !important; // 弹窗外壳透明，白色由内部 .preview-content 提供
	box-shadow: none !important;

	.el-dialog__header {
		display: none;
	}

	.el-dialog__body {
		padding: 0;
		min-height: 200px;
	}

	.preview-content {
		display: flex;
		flex-direction: row; // 左图右参，宽屏不再让参数挤压
		background: #fff;
		border-radius: 12px;
		overflow: hidden; // 圆角裁切
		width: min(880px, 80vw);
		height: min(560px, 75vh);
		margin: 0 auto;

		.preview-image-wrap {
			flex: 1 1 auto;
			min-width: 0; // 防止内部 el-image 撑破布局
			background: #1a1a2e;
			display: flex;
			justify-content: center;
			align-items: center;
			padding: 16px;

			.preview-image {
				width: 100%;
				height: 100%;
				display: flex;
				justify-content: center;
				align-items: center;

				img {
					max-width: 100%;
					max-height: 100%;
					display: block;
					object-fit: contain;
				}
			}
		}

		.preview-info {
			flex: 0 0 360px; // 右栏固定宽度，参数完整展示
			display: flex;
			flex-direction: column;
			min-height: 0;
			background: #fff;
			border-left: 1px solid #f0f0f0;

			.preview-info__scroll {
				flex: 1;
				min-height: 0;
				overflow-y: auto;
				padding: 24px;

				// 滚动条始终可见，避免误以为内容截断
				&::-webkit-scrollbar {
					width: 6px;
				}
				&::-webkit-scrollbar-thumb {
					background: #d4d4d8;
					border-radius: 3px;
				}
				&::-webkit-scrollbar-track {
					background: transparent;
				}
			}

			.preview-prompt {
				font-size: 14px;
				color: #333;
				margin: 0 0 12px;
				word-break: break-word;
				white-space: pre-wrap; // 保留提示词原始换行
				line-height: 1.6;
			}

			.preview-meta {
				display: flex;
				align-items: center;
				gap: 12px;
				margin: 0 0 16px;
				flex-wrap: wrap;
			}

			.preview-model {
				font-size: 12px;
				color: #6126f5;
				background: #f0ebff;
				padding: 2px 10px;
				border-radius: 10px;

				i {
					margin-right: 3px;
				}
			}

			.preview-time {
				font-size: 12px;
				color: #999;
			}

			.preview-actions {
				flex-shrink: 0;
				display: flex;
				justify-content: center;
				gap: 12px;
				padding: 16px 24px;
				border-top: 1px solid #f0f0f0;
				background: #fff;

				.preview-btn {
					display: inline-flex;
					align-items: center;
					gap: 6px;
					padding: 8px 20px;
					border-radius: 20px;
					font-size: 13px;
					text-decoration: none;
					transition: all 0.2s;
					cursor: pointer;
					user-select: none;

					&.primary {
						background: linear-gradient(135deg, #6126f5, #9b59f5);
						color: #fff;

						&:hover {
							transform: translateY(-1px);
							box-shadow: 0 4px 12px rgba(97, 38, 245, 0.3);
						}

						&.downloading {
							opacity: 0.75;
							cursor: not-allowed;
							pointer-events: none;
						}
					}
				}
			}
		}

		// 窄屏退回上下布局，避免在小尺寸下右栏挤掉图片
		@media (max-width: 768px) {
			flex-direction: column;
			width: 92vw;
			height: 90vh;

			.preview-image-wrap {
				flex: 0 0 50%;
				padding: 8px;
			}

			.preview-info {
				flex: 1 1 50%;
				border-left: none;
				border-top: 1px solid #f0f0f0;
			}
		}
	}
}

@keyframes pulse-ring {
	0% { transform: scale(0.8); opacity: 1; }
	100% { transform: scale(1.5); opacity: 0; }
}

@keyframes spin {
	to { transform: rotate(360deg); }
}
</style>
